import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, FileDown, FileSpreadsheet } from 'lucide-react';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatBRL, formatDateTime } from '../../lib/format';
import { Alert, Card, CardHeader, EmptyState, Input, PageHeader, Spinner, cx } from '../../ui/primitives';
import type { TenantRestaurant } from '../TenantApp';

type Column = { key: string; label: string; type: 'text' | 'money' | 'int' | 'datetime' | 'date' | 'decimal' | 'pct' };
type Cell = string | number | null;
type Report = { key: string; title: string; columns: Column[]; rows: Record<string, Cell>[]; totals?: Record<string, Cell>; notes?: string[]; period: { from: string; to: string } };
type Catalog = { canExport: boolean; reports: { key: string; title: string; group: string }[] };

function localISO(tz: string, offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function show(col: Column, v: Cell, tz: string) {
  if (v === null || v === undefined || v === '') return col.type === 'money' ? '—' : '';
  if (typeof v === 'string' && col.type !== 'text' && col.type !== 'date' && col.type !== 'datetime') return v;
  switch (col.type) {
    case 'money': return formatBRL(v as number);
    case 'pct': return `${((v as number) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
    case 'decimal': return (v as number).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    // Na linha de totais a 1ª coluna traz "Total" (texto, não data): formatar como data quebrava a tela.
    case 'datetime': return typeof v === 'string' && Number.isNaN(Date.parse(v)) ? v : formatDateTime(v as string, tz);
    case 'date': return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split('-').reverse().join('/') : String(v);
    default: return String(v);
  }
}
const numeric = (c: Column) => c.type !== 'text' && c.type !== 'date' && c.type !== 'datetime';

export function StaffReports({ restaurant }: { restaurant: TenantRestaurant }) {
  const tz = restaurant.timezone;
  const catalog = useQuery({ queryKey: ['staff-reports-catalog'], queryFn: () => api<Catalog>('/api/staff/reports') });
  const [selected, setSelected] = useState<string | null>(null);
  const [preset, setPreset] = useState<'today' | 'yesterday' | '7d' | 'month' | 'custom'>('today');
  const [custom, setCustom] = useState({ from: localISO(tz), to: localISO(tz) });
  const period = useMemo(() => {
    if (preset === 'today') return { from: localISO(tz), to: localISO(tz) };
    if (preset === 'yesterday') return { from: localISO(tz, -1), to: localISO(tz, -1) };
    if (preset === '7d') return { from: localISO(tz, -6), to: localISO(tz) };
    if (preset === 'month') return { from: `${localISO(tz).slice(0, 8)}01`, to: localISO(tz) };
    return custom;
  }, [preset, custom, tz]);
  const current = selected ?? catalog.data?.reports[0]?.key ?? null;
  const qs = `from=${period.from}&to=${period.to}`;
  const report = useQuery({
    queryKey: ['staff-report', current, qs],
    queryFn: () => api<Report>(`/api/staff/reports/${current}?${qs}`),
    enabled: !!current,
  });

  if (catalog.isPending) return <Spinner />;
  if (catalog.isError) return <Alert tone="danger">{errorMessage(catalog.error)}</Alert>;
  const groups = [...new Set(catalog.data.reports.map((r) => r.group))];

  return (
    <>
      <PageHeader title="Relatórios" description="Valores lançados, cobrados e recebidos aparecem separados. Pendentes e recusados nunca contam como receita." />
      {catalog.data.reports.length === 0 ? (
        <Card><EmptyState icon={<BarChart3 className="size-5" />} title="Nenhum relatório disponível para a sua função" /></Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_1fr]">
          <Card className="self-start">
            {groups.map((g) => (
              <div key={g} className="border-b border-line p-2 last:border-0">
                <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-ink-3">{g}</p>
                {catalog.data.reports.filter((r) => r.group === g).map((r) => (
                  <button key={r.key} onClick={() => setSelected(r.key)} className={cx('block w-full rounded-lg px-2 py-1.5 text-left text-sm', current === r.key ? 'bg-paper-2 font-medium' : 'text-ink-2 hover:bg-paper')}>
                    {r.title}
                  </button>
                ))}
              </div>
            ))}
          </Card>

          <div className="min-w-0 space-y-4">
            <Card className="flex flex-wrap items-end gap-2 p-4">
              <div className="flex flex-wrap gap-1 rounded-xl bg-paper-2 p-1 text-sm">
                {([['today', 'Hoje'], ['yesterday', 'Ontem'], ['7d', '7 dias'], ['month', 'Este mês'], ['custom', 'Período']] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setPreset(k)} className={cx('rounded-lg px-3 py-1.5 font-medium', preset === k ? 'bg-white shadow-sm' : 'text-ink-3')}>{l}</button>
                ))}
              </div>
              {preset === 'custom' && (
                <>
                  <Input type="date" aria-label="De" className="w-40" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
                  <Input type="date" aria-label="Até" className="w-40" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
                </>
              )}
              {catalog.data.canExport && current && (
                <div className="ml-auto flex gap-2">
                  <a href={apiUrl(`/api/staff/reports/${current}?${qs}&format=csv`)} className="inline-flex h-10 items-center gap-2 rounded-xl border border-line bg-white px-3 text-sm font-medium hover:bg-paper-2"><FileSpreadsheet className="size-4" /> CSV</a>
                  <a href={apiUrl(`/api/staff/reports/${current}?${qs}&format=pdf`)} className="inline-flex h-10 items-center gap-2 rounded-xl border border-line bg-white px-3 text-sm font-medium hover:bg-paper-2"><FileDown className="size-4" /> PDF</a>
                </div>
              )}
            </Card>

            {report.isPending && <Spinner />}
            {report.isError && <Alert tone="danger">{errorMessage(report.error)}</Alert>}
            {report.data && (
              <Card>
                <CardHeader title={report.data.title} description={`${show({ key: '', label: '', type: 'date' }, report.data.period.from, tz)} a ${show({ key: '', label: '', type: 'date' }, report.data.period.to, tz)}`} />
                {report.data.rows.length === 0 ? (
                  <EmptyState title="Nenhum registro no período" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-3">
                          {report.data.columns.map((c) => <th key={c.key} className={cx('whitespace-nowrap px-4 py-2 font-medium', numeric(c) ? 'text-right' : 'text-left')}>{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {report.data.rows.map((r, i) => (
                          <tr key={i} className="hover:bg-paper/60">
                            {report.data!.columns.map((c) => <td key={c.key} className={cx('px-4 py-2', numeric(c) ? 'whitespace-nowrap text-right tabular-nums' : '')}>{show(c, r[c.key] ?? null, tz)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                      {report.data.totals && (
                        <tfoot>
                          <tr className="border-t-2 border-ink/70 font-semibold">
                            {report.data.columns.map((c) => <td key={c.key} className={cx('px-4 py-2', numeric(c) ? 'whitespace-nowrap text-right tabular-nums' : '')}>{show(c, report.data!.totals![c.key] ?? null, tz)}</td>)}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
                {report.data.notes && report.data.notes.length > 0 && (
                  <ul className="space-y-1 border-t border-line px-5 py-3 text-xs text-ink-3">{report.data.notes.map((n) => <li key={n}>• {n}</li>)}</ul>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}
